import {findKeyByValue} from 'common/util/typescript';
import {EventFrequencyClause} from 'common/widgets/cohorts-query-builder/models/builder/event-frequency-clause';
import {PeoplePropertyClause} from 'common/widgets/cohorts-query-builder/models/builder/people-property-clause';
import {PropertyFilter} from 'common/widgets/cohorts-query-builder/models/builder/property-filter';
import {PropertyFilter as CommonPropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  BehaviorCountType,
  LogicalOperator,
  Property,
} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {FilterValue} from 'common/widgets/cohorts-query-builder/models/types/expression-types';
import {DatetimeOperator, FrequencyOperator} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {ClauseOperators, FilterOperator} from 'common/widgets/cohorts-query-builder/models/types/builder-state-types';
import {ANY_EVENT} from 'common/widgets/query-block/events-menu/constants';
import {
  EventFilters,
  FiltersOperator,
  QueryBehaviorEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {
  BooleanOperator,
  FilterOperator as CommonFilterOperator,
  FilterValue as CommonFilterValue,
  ListOperator,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {PRIMATIVE_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {renameEvent} from 'common/report/util/events';
import {CustomProperty, CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {BaseTimeUnit, DateRangeType, RelativeDate, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {customEventsStore} from 'common/report/stores/custom-events';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {ClausePosition, StagedCohort} from 'common/widgets/cohorts-query-builder/models/builder/staged-cohort';
import {valueToSerializedDatetime} from 'common/widgets/cohorts-query-builder/util/staged-cohort';
import {DATE_PROPERTY} from 'common/widgets/query-block/properties-menu/constants';

import {
  convertDateRangeToStagedDatetime,
  convertSerializedDatetimeToDateRange,
  DatetimeOperatorToDateRangeTypeMapping,
  getDateRangeFromBehavior,
  getDateRangeLabelFromBehavior,
} from './time';

type FilterNegationModifier = (propertyFilter: CommonPropertyFilter) => CommonPropertyFilter;

export const reversibleFilterOperator: (
  a: CommonFilterOperator,
  b: CommonFilterOperator,
) => {[key: string]: FilterNegationModifier} = (a, b) => ({
  [a]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: b,
    }),
  [b]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: a,
    }),
});

export const FilterOperatorNegationMapping: {[key: string]: FilterNegationModifier} = {
  ...reversibleFilterOperator(StringOperator.Contains, StringOperator.DoesNotContain),
  ...reversibleFilterOperator(StringOperator.Equals, StringOperator.DoesNotEqual),
  ...reversibleFilterOperator(StringOperator.Set, StringOperator.NotSet),

  ...reversibleFilterOperator(NumberOperator.EqualTo, NumberOperator.NotEqualTo),
  ...reversibleFilterOperator(NumberOperator.Between, NumberOperator.NotBetween),
  ...reversibleFilterOperator(NumberOperator.AtLeast, NumberOperator.LessThan),
  ...reversibleFilterOperator(NumberOperator.GreaterThan, NumberOperator.AtMost),

  ...reversibleFilterOperator(BooleanOperator.True, BooleanOperator.False),
  ...reversibleFilterOperator(ListOperator.Contains, ListOperator.DoesNotContain),

  ...reversibleFilterOperator(DateRangeType.Between, DateRangeType.NotBetween),
  ...reversibleFilterOperator(DateRangeType.On, DateRangeType.NotOn),
  ...reversibleFilterOperator(DateRangeType.RelativeAfter, DateRangeType.NotRelativeAfter),
  ...reversibleFilterOperator(DateRangeType.Since, DateRangeType.Before),

  [DateRangeType.RelativeFuture]: (propertyFilter) => {
    const {
      window: {unit, value},
    } = propertyFilter.filterValue as Partial<DateRange>;
    /**
     * in the next X units gets negated by applying
     * a rolling `not between` filter
     */
    return new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: DateRangeType.NotBetween,
      filterValue: new DateRange({
        type: DateRangeType.NotBetween,
        rolling: true,
        from: {value: 0, unit},
        to: {value: value * -1, unit},
      }),
    });
  },

  /**
   * @todo: [pablo] `not more than X units` has an unexpected behavior
   * in the underlying arb selector, this filter would get negated
   * with a rolling `since` filter, this does not yield the same results
   */
  /** @todo [pablo]: figure out how to display this in the UI */
  [DateRangeType.RelativeBefore]: (propertyFilter) => {
    const {
      window: {unit, value},
    } = propertyFilter.filterValue as Partial<DateRange>;

    return new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: DateRangeType.Since,
      filterValue: new DateRange({
        type: DateRangeType.Since,
        rolling: true,
        from: {value, unit},
        to: null,
      }),
    });
  },
};

export const PropertyTypeToResourceTypeMapping: {[key: string]: ResourceType} = {
  [Property.Event]: ResourceType.Event,
  [Property.Cohort]: ResourceType.Cohort,
  [Property.User]: ResourceType.User,
};

export function getResourceTypeFromPropertyType(propertyType: Property): ResourceType {
  if (!PropertyTypeToResourceTypeMapping[propertyType]) {
    throw new Error(`Invalid property type ${propertyType} for resourceType conversion`);
  }

  return PropertyTypeToResourceTypeMapping[propertyType];
}

export function getFiltersOperatorFromLiteralClauseOperators(clauseOperators: ClauseOperators): FiltersOperator {
  // return `and` as a default for single clause literals
  if (!clauseOperators) {
    return FiltersOperator.And;
  }

  const [baseOperator, extendedOperator] = clauseOperators;

  if (baseOperator === null) {
    return FiltersOperator.Then;
  }

  if (baseOperator === LogicalOperator.And) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.AndAny : FiltersOperator.And;
  }

  if (baseOperator === LogicalOperator.Or) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.Or : FiltersOperator.OrAll;
  }

  throw new Error(`Invalid clauseOperators ${clauseOperators}`);
}

export function getGroupingOperatorFromLogicalOperators(
  operators: LogicalOperator[],
): Array<FiltersOperator.And | FiltersOperator.Or> {
  return operators.map((operator) => (operator === LogicalOperator.And ? FiltersOperator.And : FiltersOperator.Or));
}

export function getLiteralClauseOperatorsFromFiltersOperator(filtersOperator: FiltersOperator): ClauseOperators {
  // filtersOperator defaults to `And`
  if (!filtersOperator) {
    return [LogicalOperator.And, LogicalOperator.And];
  }

  switch (filtersOperator) {
    case FiltersOperator.Then:
      return [null, null];
    case FiltersOperator.And:
      return [LogicalOperator.And, LogicalOperator.And];
    case FiltersOperator.AndAny:
      return [LogicalOperator.And, LogicalOperator.Or];
    case FiltersOperator.OrAll:
      return [LogicalOperator.Or, LogicalOperator.And];
    case FiltersOperator.Or:
      return [LogicalOperator.Or, LogicalOperator.Or];

    default:
      throw new Error(`Invalid filtersOperator ${filtersOperator}`);
  }
}

export function getFiltersOperatorFromPropertyFilterOperator(propertyFilterOperator: LogicalOperator): FiltersOperator {
  if (propertyFilterOperator === LogicalOperator.And) {
    return FiltersOperator.And;
  }

  if (propertyFilterOperator === LogicalOperator.Or) {
    return FiltersOperator.Or;
  }

  throw new Error(`Invalid property filter operator ${propertyFilterOperator}`);
}

export const FrequencyOperatorToNumericFilterOperatorMapping: {[key in FrequencyOperator]: NumberOperator} = {
  [FrequencyOperator.AtLeast]: NumberOperator.AtLeast,
  [FrequencyOperator.Between]: NumberOperator.Between,
  [FrequencyOperator.EqualTo]: NumberOperator.EqualTo,
  [FrequencyOperator.GreaterThan]: NumberOperator.GreaterThan,
  [FrequencyOperator.LessThan]: NumberOperator.LessThan,
};

export function convertCommonPropertyFilterToCohortsPropertyFilter(filter: CommonPropertyFilter): PropertyFilter {
  let operator: FilterOperator = filter.filterOperator as any;
  let value: FilterValue = filter.filterValue;
  let negated = false;

  if (filter.propertyType === PropertyType.Datetime) {
    operator = findKeyByValue(DatetimeOperatorToDateRangeTypeMapping, filter.filterOperator);

    // Attempt to map to negated value
    if (!operator) {
      operator = findKeyByValue(
        DatetimeOperatorToDateRangeTypeMapping,
        FilterOperatorNegationMapping[filter.filterOperator](filter).filterOperator,
      );

      if (!operator) {
        throw new Error(`Invalid filter operator ${filter.filterOperator}`);
      }

      negated = true;
      value = convertDateRangeToStagedDatetime(filter.filterValue as DateRange, operator);
    } else if (operator === DatetimeOperator.On && DateRange.isRelativeDate((filter.filterValue as DateRange).from)) {
      // handle the `not more than [X unit] ago` transformation
      const fromWindow = (filter.filterValue as DateRange).from as RelativeDate;
      negated = true;
      operator = DatetimeOperator.RelativeBeforeInPast;
      value = {
        window: fromWindow,
      };
    } else {
      value = convertDateRangeToStagedDatetime(filter.filterValue as DateRange, operator);
    }

    // Special-case for future dates
    if (
      operator === DatetimeOperator.Relative &&
      typeof value !== `string` &&
      !Array.isArray(value) &&
      value.window?.value < 0
    ) {
      operator = DatetimeOperator.RelativeFuture;
    }
  }

  const propertyFilter = new PropertyFilter({
    operator,
    type: filter.propertyType,
    property: filter.propertyName,
    value,
    propertyType: findKeyByValue(PropertyTypeToResourceTypeMapping, filter.resourceType) as Property,
    ...(filter.customPropertyId
      ? {
          customPropertyId: filter.customPropertyId,
        }
      : {}),
    dataGroupId: filter.dataGroupId,
    joinPropertyType: filter.joinPropertyType,
    negated,
  });

  if (
    new PropertyFilter()
      .setStagedFilterClause({...propertyFilter, joinPropertyType: propertyFilter.joinPropertyType as any})
      .hasInvalidStagedFilterClause()
  ) {
    throw new Error(`Invalid propertyFilter, staged clause includes invalid values`);
  }

  return propertyFilter;
}

export function convertPropertyFilterToCommon(filter: PropertyFilter, forceNegated = false): CommonPropertyFilter {
  let filterValue: CommonFilterValue = filter.value;
  let filterOperator: CommonFilterOperator;

  // Transform filter types
  switch (filter.type) {
    case PropertyType.Boolean:
      filterOperator = filter.operator as BooleanOperator;
      break;
    case PropertyType.Datetime:
      filterOperator = DatetimeOperatorToDateRangeTypeMapping[filter.operator as DatetimeOperator];
      filterValue = convertSerializedDatetimeToDateRange(
        valueToSerializedDatetime(filter.value, filter.operator as DatetimeOperator),
        filter.operator as DatetimeOperator,
      );
      break;
    case PropertyType.List:
      filterOperator = filter.operator as ListOperator;
      break;
    case PropertyType.String:
      filterOperator = filter.operator as StringOperator;
      break;
    case PropertyType.Number:
      filterOperator = filter.operator as NumberOperator;
      break;
    case PropertyType.Dimension:
      filterOperator = filter.operator as any;
      break;
    default:
      throw new Error(`Unhandled property filter type in filter ${JSON.stringify({filter})}`);
  }

  const propertyFilter = new CommonPropertyFilter({
    filterOperator,
    filterValue,
    propertyName: filter.property,
    ...(filter.customPropertyId
      ? {
          customPropertyId: filter.customPropertyId,
        }
      : {}),
    propertyType: filter.type,
    resourceType: getResourceTypeFromPropertyType(filter.propertyType),
    propertyDefaultType: filter.type,
    dataGroupId: filter.dataGroupId,
    joinPropertyType: filter.joinPropertyType as PropertyType,
  });

  // Operator negations are only applied to filters under People property clauses
  // Event frequency clauses are negated with a frequency of 0 and cohort clauses have negated properties built-in
  return forceNegated || filter.isNegated()
    ? FilterOperatorNegationMapping[filterOperator](propertyFilter)
    : propertyFilter;
}

export function getEventFromCohortEvent(event): EventItem {
  if (!event) {
    return ANY_EVENT.value;
  }

  if (event.startsWith(`$custom_event:`)) {
    const id = Number(event.replace(/^\$custom_event:/, ``));

    return {
      id,
      custom: true,
      label: customEventsStore?.value[id]?.name ?? event,
      value: event,
    };
  }

  return {label: renameEvent(event), value: event};
}

export function getCohortEventFromEventItem(event: EventItem): string {
  if (event.value === ANY_EVENT.value.value) {
    return ``;
  }

  if (event.custom) {
    return `$custom_event:${event.id}`;
  }

  return event.value;
}

export function applyCommonPropertyFilterToClause(
  filter: CommonPropertyFilter,
  clausePosition: ClausePosition,
  propertyFilterIdx: number,
  stagedCohort: StagedCohort,
) {
  const propertyFilter = convertCommonPropertyFilterToCohortsPropertyFilter(filter);
  return stagedCohort
    .addPropertyFilterToClause(clausePosition)
    .updateStagedFilterClause(
      {
        ...propertyFilter,
        joinPropertyType: propertyFilter.joinPropertyType as any,
      },
      clausePosition,
      propertyFilterIdx,
    )
    .commitStagedFilterClause(clausePosition, propertyFilterIdx);
}

export function applyCommonPropertyFiltersToStagedCohort(
  filters: EventFilters,
  clausePosition: ClausePosition,
  filtersOperator: FiltersOperator,
  stagedCohort: StagedCohort,
) {
  let updatedStagedCohort = filters.reduce(
    (stagedCohort, filter, i) => applyCommonPropertyFilterToClause(filter, clausePosition, i, stagedCohort),
    stagedCohort,
  );

  if (
    (filtersOperator === FiltersOperator.Or &&
      updatedStagedCohort.getPropertyFilterOperator(clausePosition) !== LogicalOperator.Or) ||
    (filtersOperator === FiltersOperator.And &&
      updatedStagedCohort.getPropertyFilterOperator(clausePosition) !== LogicalOperator.And)
  ) {
    updatedStagedCohort = updatedStagedCohort.togglePropertyFilterOperator(clausePosition);
  }

  return updatedStagedCohort;
}

export function applyBehaviorFilterToStagedCohort(
  filter: CommonPropertyFilter,
  clausePosition: ClausePosition,
  stagedCohort: StagedCohort,
): StagedCohort {
  const {customProperty, filterValue, filterOperator} = filter;
  const behavior: QueryBehaviorEntry = customProperty.behavior;
  let frequency: [number, number] | [number] = [0, 0];

  if (Array.isArray(filterValue) && filterValue.length === 2) {
    frequency = filterValue as [number, number];
  } else if (filterValue <= 1) {
    frequency = [filterValue] as [number];
  } else {
    frequency = [filterValue, filterValue] as [number, number];
  }

  let updatedStagedCohort = stagedCohort
    .updateEvent(getCohortEventFromEventItem(behavior.event), clausePosition)
    .updateFrequencyValue(frequency, clausePosition)
    .updateFrequencyOperator(
      findKeyByValue(FrequencyOperatorToNumericFilterOperatorMapping, filterOperator),
      clausePosition,
    );

  // TODO: Organize in time transformations
  // apply daterange to behavior
  if (behavior.dateRange.type === DateRangeType.RelativeAfter) {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorCustomRange(
      behavior.dateRange.window.unit as BaseTimeUnit,
      behavior.dateRange.window.value,
      clausePosition,
    );
  } else if (behavior.dateRange.type === DateRangeType.Since) {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorDateRange(
      behavior.dateRange.from as string,
      null,
      clausePosition,
    );
  } else {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorDateRange(
      behavior.dateRange.from as string,
      behavior.dateRange.to as string,
      clausePosition,
    );
  }

  updatedStagedCohort = applyCommonPropertyFiltersToStagedCohort(
    behavior.filters,
    clausePosition,
    behavior.filtersOperator,
    updatedStagedCohort,
  );

  return updatedStagedCohort;
}

export function getEventFiltersFromEventsFrequencyClause(clause: EventFrequencyClause): EventFilters {
  const {eventSelector, type} = clause.behavior.count;

  const additionalFilters = EventFrequencyClause.getPropertyFiltersAndOperator(
    eventSelector.selector,
  ).propertyFilters.map((filter) => convertPropertyFilterToCommon(filter));

  const event = getEventFromCohortEvent(eventSelector.event);

  const label = `${event.label} ${getDateRangeLabelFromBehavior(clause.behavior, clause.isNegated())}`;

  const behavior: QueryBehaviorEntry = {
    type: SelectorTypeDefinitions.Behavior,
    dateRange: getDateRangeFromBehavior(clause.behavior),
    event,
    filters: additionalFilters,
    filtersOperator: getFiltersOperatorFromPropertyFilterOperator(clause.propertyFilterOperator),
    aggregationOperator: PRIMATIVE_MATH_TYPES.TOTAL,
    property: null,
  };

  // transforms "on unique days" to do a count on distinct days
  if (type === BehaviorCountType.Day) {
    behavior.aggregationOperator = PRIMATIVE_MATH_TYPES.UNIQUE_VALUES;
    behavior.property = {
      label: null,
      value: DATE_PROPERTY.value,
      unit: TimeUnit.Day,
      resourceType: ResourceType.Event,
      propertyDefaultType: PropertyType.Datetime,
      type: PropertyType.Datetime,
    };
  }

  const customProperty: CustomProperty = {
    name: label,
    behavior,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: PropertyType.Number,
    resourceType: CustomPropertyResourceType.People,
  };

  const behaviorFilter = new CommonPropertyFilter({
    customProperty,
    ...(clause.isNegated()
      ? {
          filterOperator: NumberOperator.EqualTo,
          filterValue: 0,
        }
      : {
          filterOperator: FrequencyOperatorToNumericFilterOperatorMapping[clause.frequencyOperator],
          filterValue: clause.frequency.length === 1 ? clause.frequency[0] : clause.frequency,
        }),
    propertyType: PropertyType.Number,
    propertyName: label,
    resourceType: ResourceType.User,
  });

  return [behaviorFilter];
}

export function getEventFiltersFromPeoplePropertyClause(clause: PeoplePropertyClause): EventFilters {
  return clause.propertyFilters.map((filter) => convertPropertyFilterToCommon(filter));
}

export function createCohortCommonPropertyFilter(cohortId: number, negated = false) {
  return new CommonPropertyFilter({
    filterValue: [
      {
        cohort: {
          id: cohortId,
          negated,
        },
      },
    ],
    propertyType: PropertyType.List,
    filterOperator: negated ? ListOperator.DoesNotContain : ListOperator.Contains,
    resourceType: ResourceType.Cohort,
  });
}
